import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { OnboardingDocument } from "./types";
import { useStepDialog } from "./use-step-dialog";

interface DeleteDocumentModalProps {
  document: OnboardingDocument | null;
  isDeleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export const DeleteDocumentModal = ({
  document,
  isDeleting,
  error,
  onCancel,
  onConfirm,
}: DeleteDocumentModalProps) => {
  const open = useStepDialog(!!document, onCancel);
  const name = document ? document.file_name || document.title : "";
  const consequence =
    "It is removed for good. You can upload it again if you still need it.";

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        // Dismissing mid-delete would hide the outcome, so hold the modal open.
        if (!open && !isDeleting) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete document</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">
          {`Delete ${name}? ${consequence}`}
        </DialogDescription>

        <Stack align="start" className="w-full" gap={6}>
          <Text tone="muted">
            Delete <span className="font-semibold text-ink">{name}</span>?{" "}
            {consequence}
          </Text>
          {error && <Alert color="danger">{error}</Alert>}
        </Stack>

        <DialogFooter>
          <Button
            variant="secondary"
            type="button"
            disabled={isDeleting}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            type="button"
            disabled={isDeleting}
            loading={isDeleting}
            onClick={onConfirm}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
